import moment from 'moment';
import * as _ from 'lodash';
import DDLogs from '@/DDLogs';
import { isIosNativeApp } from '@/utils/deviceUtils';
import subscriptionStateApi from '../../api/SubscriptionStateApi';

import { TRIAL_PERIODS, SUBSCRIPTION_STATES } from '../../constants/subscription-state';

const LOCAL_STORAGE_KEY = 'riseup.subscriptionCtaDismissedData';

const state = {
  subscriptionState: null,
  since: null,
  until: null,
  untilWithPromo: null,
  longTermDormant: false,
};

function _shouldShowCta({ inTrial, trialPeriod }) {
  if (isIosNativeApp()) {
    return false;
  }
  if (inTrial) {
    const subscriptionDismissedDataString = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (subscriptionDismissedDataString) {
      const { periodDismissed } = JSON.parse(subscriptionDismissedDataString);
      if (!periodDismissed) {
        return true;
      }
      const currentTrialPeriod = trialPeriod;
      return currentTrialPeriod !== periodDismissed;
    }
    return true;
  }
  return false;
}

const getters = {
  daysLeft: state => Math.max(moment(state.until).diff(moment(), 'days') + 1, 0),
  daysLeftIncludingPromotion: state => Math.max(moment(state.untilWithPromo).diff(moment(), 'days') + 1, 0),
  trialLength: state => moment(state.until).diff(moment(state.since), 'days'),
  subscriptionState: state => state.subscriptionState,
  trialPeriod: (state, getters) => {
    if (getters.inTrial) {
      const diffWithUntil = moment(state.until).diff(moment(), 'days');
      if (diffWithUntil > 20) {
        return TRIAL_PERIODS.BEGINNING;
      } if (diffWithUntil <= 20 && diffWithUntil > 7) {
        return TRIAL_PERIODS.MIDDLE;
      }
      // 7 days left for the trial
      return TRIAL_PERIODS.FINAL;
    }
    return undefined;
  },
  hasAccessToCashflow: state => _.includes([
    SUBSCRIPTION_STATES.SUBSCRIBED,
    SUBSCRIPTION_STATES.CANCELED,
    SUBSCRIPTION_STATES.FREE,
    SUBSCRIPTION_STATES.TRIAL,
  ], state.subscriptionState),
  // TODO This should actually change to work with the subscription object - but at the moment we're leaving it
  // the way it is to avoid breaking changes. When we have entitlements, we want to do this differently
  isAutoRenewalOn: state => state.subscriptionState === SUBSCRIPTION_STATES.SUBSCRIBED,
  inTrial: state => state.subscriptionState === SUBSCRIPTION_STATES.TRIAL,
  subscribed: state => state.subscriptionState === SUBSCRIPTION_STATES.SUBSCRIBED,
  preTrial: state => state.subscriptionState === SUBSCRIPTION_STATES.PRE_TRIAL,
  shouldShowCta: (state, getters) => _shouldShowCta({
    inTrial: getters.inTrial,
    trialPeriod: getters.trialPeriod,
  }),
  isDormant: state => _.includes([SUBSCRIPTION_STATES.DORMANT_AFTER_TRIAL, SUBSCRIPTION_STATES.DORMANT_AFTER_CANCELED], state.subscriptionState),
};

const actions = {
  async fetchSubscriptionState({ commit }) {
    const subscriptionState = await subscriptionStateApi.fetchSubscriptionState();
    commit('setSubscriptionState', subscriptionState.state);
    commit('setSince', subscriptionState.since);
    commit('setUntil', subscriptionState.until);
    commit('setUntilWithPromo', subscriptionState.untilWithPromo);
    commit('setIsLongTermDormant', subscriptionState.longTermDormant);
  },
  async setSubscriptionStateVariables({ commit }, subscriptionStateVariables) {
    commit('setSubscriptionStateVariables', subscriptionStateVariables);
  },
  ctaDismissed({ getters }) {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ dismissDate: new Date(), periodDismissed: getters.trialPeriod }));
    } catch (error) {
      DDLogs.error(error);
    }
  },
};

// mutations
const mutations = {
  setSubscriptionState(state, subscriptionState) {
    state.subscriptionState = subscriptionState;
  },
  setHasAccessToCashflow(state) {
    // The logic here is temporary until we refactor the subscription state management
    state.subscriptionState = SUBSCRIPTION_STATES.SUBSCRIBED;
  },
  setAutoRenewalOn(state) {
    // The logic here is temporary until we refactor the subscription state management
    state.subscriptionState = SUBSCRIPTION_STATES.SUBSCRIBED;
  },
  setSince(state, since) {
    state.since = since;
  },
  setUntil(state, until) {
    state.until = until;
  },
  setUntilWithPromo(state, untilWithPromo) {
    state.untilWithPromo = untilWithPromo;
  },
  setSubscriptionStateVariables(state, { subscriptionState }) {
    state.subscriptionState = subscriptionState?.state ?? null;
    state.since = subscriptionState?.since ?? null;
    state.until = subscriptionState?.until ?? null;
    state.untilWithPromo = subscriptionState?.untilWithPromo ?? null;
  },
  setIsLongTermDormant(state, isLongTermDormant) {
    state.longTermDormant = isLongTermDormant;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
